import { useEffect, useRef } from 'react'
import { Link, Outlet, useLocation, useNavigationType } from 'react-router-dom'
import { ErrorBoundary } from './ErrorBoundary'

/**
 * A client-side route change moves neither scroll position nor focus on its own,
 * which leaves two problems the browser would otherwise have solved:
 *
 *  - scrolled halfway down the results, clicking a row opened the record halfway
 *    down the record
 *  - focus stayed on a link that no longer exists, so a keyboard or screen-reader
 *    user was given no indication the page had changed
 */
function useRouteChange(mainRef: React.RefObject<HTMLElement | null>) {
  const { pathname } = useLocation()
  const navigationType = useNavigationType()
  const lastPathname = useRef(pathname)

  useEffect(() => {
    // A filter edit is a REPLACE that keeps the same pathname, so it must not
    // scroll the reader to the top or move their focus. Comparing the pathname
    // covers that and the initial render in one guard.
    if (lastPathname.current === pathname) return
    lastPathname.current = pathname

    // On back/forward, leave the browser to restore where the reader was.
    if (navigationType !== 'POP') {
      window.scrollTo(0, 0)
    }

    mainRef.current?.focus()
  }, [pathname, navigationType, mainRef])
}

export function Layout() {
  const mainRef = useRef<HTMLElement>(null)
  const { pathname } = useLocation()
  useRouteChange(mainRef)

  return (
    <>
      <a className="skip-link" href="#main">
        Skip to main content
      </a>
      <header className="site-header">
        <div className="site-width">
          <Link to="/search" className="site-title">
            Political Advertisement Disclosure Archive
          </Link>
        </div>
      </header>
      {/* tabIndex allows focus to be moved here on navigation; it is not in the
          tab order. */}
      <main id="main" ref={mainRef} tabIndex={-1} className="site-width">
        {/* Keyed on the path so navigating away from a failed page clears the error
            instead of stranding the reader on it. The header and footer stay put. */}
        <ErrorBoundary key={pathname}>
          <Outlet />
        </ErrorBoundary>
      </main>
      <footer className="site-footer">
        <div className="site-width">
          <p>
            This archive is published to meet state political advertising disclosure requirements.
            Records remain published for five years. A record is updated if its disclosure details
            are later corrected, so each page shows the version currently on file.
          </p>
        </div>
      </footer>
    </>
  )
}
